/* eslint-disable max-lines-per-function */
/**
 * @file KeyboardNavigationIndicator.tsx
 * @description Visual indicator and shortcut reference for keyboard navigation mode
 *
 * Shows when Tab is pressed to enter navigation mode
 * Displays available shortcuts in a tooltip
 */

import { Keyboard } from "lucide-react";
import { useEffect, useState, type ReactElement } from "react";
import { useLocation } from "react-router-dom";
import { useKeyboardNavigation } from "../contexts/KeyboardNavigationContext";

export function KeyboardNavigationIndicator(): ReactElement {
  const { isNavigationMode } = useKeyboardNavigation();
  const location = useLocation();
  const [showToast, setShowToast] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);

  // Don't show toast on Title pages (nav mode changes automatically)
  const isTitlePage = location.pathname.startsWith("/home/title/");

  // Show toast briefly when entering navigation mode (except on Title pages)
  useEffect(() => {
    if (isNavigationMode && !isTitlePage) {
      setShowToast(true);
      const timer = setTimeout(() => setShowToast(false), 2500);
      return () => clearTimeout(timer);
    } else {
      setShowToast(false);
    }
  }, [isNavigationMode, isTitlePage]);

  if (!isNavigationMode) {
    return <></>;
  }

  return (
    <>
      {/* Toast notification */}
      {showToast && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-[60] pointer-events-none">
          <div className="bg-neutral-900/95 backdrop-blur-md border border-lumi-accent/30 rounded-lg px-4 py-2 shadow-2xl animate-in fade-in slide-in-from-top-2 duration-300">
            <div className="flex items-center gap-2 text-sm text-white">
              <Keyboard size={16} className="text-lumi-accent" />
              <span className="font-medium">Navigation Mode Active</span>
              <span className="text-neutral-400 ml-2">Press Tab or Esc to exit</span>
            </div>
          </div>
        </div>
      )}

      {/* Persistent indicator with tooltip */}
      <div className="fixed bottom-6 right-6 z-[60]">
        <button
          type="button"
          className="relative"
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
          aria-label="Keyboard navigation shortcuts"
        >
          {/* Indicator button */}
          <div className="bg-neutral-900/90 backdrop-blur-md border border-lumi-accent/40 rounded-full p-2 shadow-xl">
            <Keyboard size={18} className="text-lumi-accent" />
          </div>

          {/* Tooltip with shortcuts */}
          {showTooltip && (
            <div className="absolute bottom-full right-0 mb-2 w-64 bg-neutral-900/98 backdrop-blur-md border border-neutral-700 rounded-lg shadow-2xl p-3 animate-in fade-in slide-in-from-bottom-2 duration-200">
              <div className="text-xs font-semibold text-lumi-accent mb-2">Navigation Shortcuts</div>
              <div className="space-y-1.5 text-xs text-neutral-300">
                <div className="flex items-center justify-between">
                  <span>Move between cards</span>
                  <kbd className="px-1.5 py-0.5 bg-neutral-800 border border-neutral-700 rounded text-[10px] font-mono">
                    ← →
                  </kbd>
                </div>
                <div className="flex items-center justify-between">
                  <span>Move between rails</span>
                  <kbd className="px-1.5 py-0.5 bg-neutral-800 border border-neutral-700 rounded text-[10px] font-mono">
                    ↑ ↓
                  </kbd>
                </div>
                <div className="flex items-center justify-between">
                  <span>Play / Resume</span>
                  <kbd className="px-1.5 py-0.5 bg-neutral-800 border border-neutral-700 rounded text-[10px] font-mono">
                    Enter
                  </kbd>
                </div>
                <div className="flex items-center justify-between">
                  <span>More info</span>
                  <kbd className="px-1.5 py-0.5 bg-neutral-800 border border-neutral-700 rounded text-[10px] font-mono">
                    i
                  </kbd>
                </div>
                <div className="flex items-center justify-between">
                  <span>Toggle watchlist</span>
                  <kbd className="px-1.5 py-0.5 bg-neutral-800 border border-neutral-700 rounded text-[10px] font-mono">
                    w
                  </kbd>
                </div>
                <div className="flex items-center justify-between">
                  <span>First / Last card</span>
                  <kbd className="px-1.5 py-0.5 bg-neutral-800 border border-neutral-700 rounded text-[10px] font-mono">
                    Home / End
                  </kbd>
                </div>
                <div className="h-px bg-neutral-800 my-2" />
                <div className="flex items-center justify-between">
                  <span>Exit navigation mode</span>
                  <kbd className="px-1.5 py-0.5 bg-neutral-800 border border-neutral-700 rounded text-[10px] font-mono">
                    Tab / Esc
                  </kbd>
                </div>
              </div>
            </div>
          )}
        </button>
      </div>
    </>
  );
}
